import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { selectZefDialogState, zefDialogClose } from '@zerops/zef/dialog';
import { ZefReactiveComponent } from '@zerops/zef/core';
import { ProjectBaseApi } from '@zerops/zerops/core/project-base';
import { EMPTY, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { EmbeddedUiOverlayComponent } from '../embedded-ui-overlay';
import { FEATURE_NAME } from './victoria-logs-overlay.constant';
import { VictoriaLogsOverlayMeta } from './victoria-logs-overlay.model';
import { VictoriaLogsOverlayService } from './victoria-logs-overlay.service';

@Component({
  selector: 'z-victoria-logs-overlay',
  templateUrl: './victoria-logs-overlay.feature.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ EmbeddedUiOverlayComponent ]
})
export class VictoriaLogsOverlayFeature extends ZefReactiveComponent {

  // # Deps
  #store = inject(Store);
  #api = inject(ProjectBaseApi);
  overlay = inject(VictoriaLogsOverlayService);

  // # Event Streams
  onClose$ = new Subject<void>();
  onReload$ = new Subject<void>();

  // # Data
  // -- sync
  key = FEATURE_NAME;
  url = signal<string | null>(null);
  projectName = signal('');
  loading = signal(false);
  #loadedProjectId: string | null = null;

  // -- async
  dialogState$ = this.#store.pipe(select(selectZefDialogState(FEATURE_NAME)));
  open$ = this.dialogState$.pipe(map((data) => !!data?.state));
  openMeta$ = this.dialogState$.pipe(
    filter((data) => !!data?.state),
    map((data) => data.meta as VictoriaLogsOverlayMeta | undefined),
    filter((meta): meta is VictoriaLogsOverlayMeta => !!meta?.projectId)
  );

  // # Dispatcher
  constructor() {
    super();

    this.$dispatchActions([
      this.onClose$.pipe(
        tap(() => this.#reset()),
        map(() => zefDialogClose({ key: FEATURE_NAME }))
      ),

      // Re-opening for the project whose frame is alive only brings it forward and keeps the iframe's internal state.
      this.openMeta$.pipe(
        tap((meta) => {
          this.overlay.open.set(true);
          this.overlay.restore();
          this.projectName.set(meta.projectName || '');
        }),
        filter((meta) => !(this.#loadedProjectId === meta.projectId && !!this.url())),
        tap((meta) => this.#loadedProjectId = meta.projectId),
        switchMap((meta) => this.#load$(meta.projectId))
      ),

      this.onReload$.pipe(
        filter(() => !!this.#loadedProjectId),
        switchMap(() => this.#load$(this.#loadedProjectId))
      ),

      // Dialog closed from elsewhere
      this.open$.pipe(
        filter((open) => !open),
        tap(() => this.#reset()),
        map(() => null),
        filter((v) => v !== null)
      )
    ]);
  }

  openInNewTab() {
    const url = this.url();
    if (url) { window.open(url, '_blank', 'noopener,noreferrer'); }
  }

  // On failure the previous URL stays, so a failed reload leaves the live frame alone and only the first load shows the empty state.
  #load$(projectId: string) {
    this.loading.set(true);
    return this.#api.logUrl$(projectId).pipe(
      tap((log) => {
        this.loading.set(false);
        if (log?.urlUi) { this.url.set(log.urlUi); }
      }),
      catchError(() => {
        this.loading.set(false);
        return EMPTY;
      }),
      map(() => null),
      filter((v): v is never => v !== null)
    );
  }

  #reset() {
    this.overlay.reset();
    this.url.set(null);
    this.projectName.set('');
    this.loading.set(false);
    this.#loadedProjectId = null;
  }

}
